import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import SearchBar from "./SearchBar";
import { getFundamentals, getPrice, getAaaCorporateYield, getCompanyLogo } from "../services/api";
import { companyLogoTitleStyle, COMPANY_LOGO_TITLE_SIZE, COMPANY_LOGO_TITLE_SIZE_MOBILE } from "../utils/chartCompanyLogo";
import { aggregateToInterval } from "../utils/time";
import { parseAaaYieldApiResponse } from "../utils/fundamentalMetrics";
import {
  buildFilledValuesByModel,
  buildHistoricalIntrinsicSeries,
  lastFiniteValue,
} from "../utils/intrinsicValueFromFundamentals";
import {
  INTRINSIC_MODEL_ORDER,
  INTRINSIC_MODELS,
  computeIntrinsicForModel,
  defaultValuesForModel,
} from "../utils/intrinsicValueModels";
import { runDeviationDcaBacktest } from "../utils/intrinsicBacktest";

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  mid: "#6b6b7b",
  accent: "#6366f1",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
};

const CHART_BG = BRAND.panel;

/** Optional `minTimeSec`: no intrinsic before first fundamentals snapshot (aligns with historical mode). */
function buildIntrinsicOverTime(pxSec, iv0, cagrDecimal, minTimeSec) {
  if (!pxSec?.length || !(iv0 > 0)) return [];
  const t0 = pxSec[0].time;
  const secPerYear = 365.25 * 24 * 3600;
  return pxSec.map((p) => {
    if (minTimeSec != null && Number.isFinite(minTimeSec) && p.time < minTimeSec) {
      return [p.time * 1000, null];
    }
    const years = (p.time - t0) / secPerYear;
    const v = iv0 * Math.pow(1 + cagrDecimal, years);
    return [p.time * 1000, v];
  });
}

function buildDeviationPercent(pxSec, intrinsicPairs) {
  if (!pxSec?.length || !intrinsicPairs?.length) return [];
  const out = [];
  for (let i = 0; i < pxSec.length; i++) {
    const price = pxSec[i].value;
    const iv = intrinsicPairs[i]?.[1];
    const t = pxSec[i].time * 1000;
    if (iv > 0 && Number.isFinite(price)) {
      out.push([t, ((price - iv) / iv) * 100]);
    } else {
      out.push([t, null]);
    }
  }
  return out;
}

export default function IntrinsicValuePage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [ticker, setTicker] = useState("AAPL");
  const [modelId, setModelId] = useState("fcff");
  const [fieldValues, setFieldValues] = useState(() => defaultValuesForModel("fcff"));
  const [intrinsicCagrPct, setIntrinsicCagrPct] = useState(0);
  const [pxSec, setPxSec] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [computedIv, setComputedIv] = useState(null);
  const [computeError, setComputeError] = useState(null);
  const [labData, setLabData] = useState(null);
  const [fundLoading, setFundLoading] = useState(false);
  const [fundError, setFundError] = useState(null);
  const [aaaRes, setAaaRes] = useState(null);
  const [useHistoricalIntrinsic, setUseHistoricalIntrinsic] = useState(true);
  const [logScale, setLogScale] = useState(true);
  const [backtestInitial, setBacktestInitial] = useState(10000);
  const [backtestDcaDaily, setBacktestDcaDaily] = useState(100);
  const chartRef = useRef(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  const aaaParsed = useMemo(() => parseAaaYieldApiResponse(aaaRes || { points: [], fallbackPct: 5 }), [aaaRes]);

  useEffect(() => {
    let cancelled = false;
    const sym = ticker?.trim().toUpperCase();
    setCompanyLogoUrl(null);
    if (!sym) return undefined;
    getCompanyLogo(sym).then((url) => {
      if (!cancelled) setCompanyLogoUrl(url);
    });
    return () => { cancelled = true; };
  }, [ticker]);

  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ticker?.trim()) return;
      setPriceLoading(true);
      setPriceError(null);
      try {
        const raw = await getPrice(ticker.trim().toUpperCase(), { withVolume: false, timeseries: 20000 });
        if (cancelled) return;
        const cleaned = (raw || []).filter((p) => p?.date && p?.close != null && Number.isFinite(Number(p.close)));
        const daily = aggregateToInterval(cleaned, "daily");
        setPxSec(daily);
      } catch (e) {
        if (!cancelled) setPriceError(e?.message || "Could not load price.");
        setPxSec([]);
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ticker?.trim()) return;
      setFundLoading(true);
      setFundError(null);
      setLabData(null);
      try {
        const [fund, aaa] = await Promise.all([
          getFundamentals(ticker.trim().toUpperCase()),
          getAaaCorporateYield().catch(() => ({ points: [], fallbackPct: 5 })),
        ]);
        if (cancelled) return;
        setAaaRes(aaa);
        const built = buildFilledValuesByModel(fund || {}, aaa);
        setLabData(built);
      } catch (e) {
        if (!cancelled) setFundError(e?.message || "Could not load fundamentals.");
        setAaaRes({ points: [], fallbackPct: 5 });
      } finally {
        if (!cancelled) setFundLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  useEffect(() => {
    if (labData?.filledByModel?.[modelId]) {
      setFieldValues(labData.filledByModel[modelId]);
    } else {
      setFieldValues(defaultValuesForModel(modelId));
    }
  }, [modelId, labData]);

  const meta = INTRINSIC_MODELS[modelId];

  const handleApply = () => {
    setComputeError(null);
    setUseHistoricalIntrinsic(false);
    const res = computeIntrinsicForModel(modelId, fieldValues);
    if (res.error) {
      setComputedIv(null);
      setComputeError(res.error);
      return;
    }
    setComputedIv(res.ivPerShare);
  };

  const handleFillFromData = () => {
    if (labData?.filledByModel?.[modelId]) {
      setFieldValues(labData.filledByModel[modelId]);
      setUseHistoricalIntrinsic(true);
      setComputeError(null);
    }
  };

  const cagrDec = intrinsicCagrPct / 100;

  const historicalIntrinsicSeries = useMemo(() => {
    if (!useHistoricalIntrinsic || !labData?.snapshots?.length || !labData?.rates || !pxSec.length) return [];
    return buildHistoricalIntrinsicSeries(
      modelId,
      pxSec,
      labData.snapshots,
      labData.rates,
      aaaParsed.points,
      aaaParsed.fallbackPct
    );
  }, [useHistoricalIntrinsic, modelId, pxSec, labData, aaaParsed.points, aaaParsed.fallbackPct]);

  const manualIntrinsicSeries = useMemo(() => {
    if (computedIv == null || !pxSec.length) return [];
    const minT = labData?.snapshots?.[0]?.time;
    return buildIntrinsicOverTime(pxSec, computedIv, cagrDec, minT);
  }, [pxSec, computedIv, cagrDec, labData?.snapshots]);

  const intrinsicSeries = useMemo(() => {
    const histHas = historicalIntrinsicSeries.some((p) => p[1] != null && Number.isFinite(p[1]));
    if (useHistoricalIntrinsic && histHas) return historicalIntrinsicSeries;
    return manualIntrinsicSeries;
  }, [useHistoricalIntrinsic, historicalIntrinsicSeries, manualIntrinsicSeries]);

  const displayIv = useMemo(() => {
    if (useHistoricalIntrinsic && historicalIntrinsicSeries.length) {
      const last = lastFiniteValue(historicalIntrinsicSeries);
      if (last != null) return last;
    }
    return computedIv;
  }, [useHistoricalIntrinsic, historicalIntrinsicSeries, computedIv]);

  const priceSeries = useMemo(
    () => pxSec.map((p) => [p.time * 1000, p.value]),
    [pxSec]
  );

  const deviationSeries = useMemo(
    () => buildDeviationPercent(pxSec, intrinsicSeries),
    [pxSec, intrinsicSeries]
  );

  const chartOptions = useMemo(() => {
    const hasIntrinsic = intrinsicSeries.some((p) => p[1] != null && Number.isFinite(p[1]));
    return {
      chart: {
        backgroundColor: CHART_BG,
        height: isMobile ? 420 : 520,
        spacingRight: 12,
      },
      title: { text: null },
      legend: {
        enabled: true,
        itemStyle: { color: BRAND.textSecondary },
        itemHoverStyle: { color: BRAND.text },
      },
      rangeSelector: {
        selected: 3,
        inputEnabled: false,
        buttonTheme: {
          fill: BRAND.panel,
          stroke: BRAND.border,
          style: { color: BRAND.text },
          states: { select: { fill: BRAND.text, style: { color: "#1b1b1f" } } },
        },
        labelStyle: { color: BRAND.muted },
      },
      navigator: {
        outlineColor: BRAND.border,
        series: { color: BRAND.muted },
      },
      scrollbar: {
        barBackgroundColor: BRAND.mid,
        buttonBackgroundColor: BRAND.mid,
        trackBorderColor: BRAND.border,
      },
      xAxis: {
        type: "datetime",
        lineColor: BRAND.border,
        tickColor: BRAND.border,
        labels: { style: { color: BRAND.muted } },
      },
      yAxis: [
        {
          height: "62%",
          resize: { enabled: true },
          type: logScale ? "logarithmic" : "linear",
          title: { text: logScale ? "Price & intrinsic ($, log)" : "Price & intrinsic ($)", style: { color: BRAND.text } },
          labels: {
            align: "right",
            x: -4,
            style: { color: BRAND.muted },
          },
          gridLineColor: "#2f2f34",
          opposite: true,
          startOnTick: false,
          endOnTick: false,
        },
        {
          top: "68%",
          height: "28%",
          offset: 0,
          title: { text: "Deviation (price vs IV)", style: { color: BRAND.text } },
          labels: {
            format: "{value}%",
            align: "right",
            x: -4,
            style: { color: BRAND.muted },
          },
          gridLineColor: "#2f2f34",
          opposite: true,
          plotLines: hasIntrinsic
            ? [
                {
                  value: 0,
                  color: BRAND.border,
                  width: 1,
                  zIndex: 3,
                  dashStyle: "Dash",
                },
              ]
            : [],
        },
      ],
      tooltip: {
        shared: true,
        split: false,
        backgroundColor: BRAND.panel,
        borderColor: BRAND.border,
        style: { color: BRAND.text },
      },
      plotOptions: {
        series: {
          animation: false,
          turboThreshold: 0,
        },
      },
      series: [
        {
          type: "line",
          id: "price",
          name: "Close",
          data: priceSeries,
          yAxis: 0,
          color: BRAND.accent,
          lineWidth: 1.4,
          tooltip: { valueDecimals: 2, valuePrefix: "$" },
          dataGrouping: { enabled: true },
        },
        ...(hasIntrinsic
          ? [
              {
                type: "line",
                name: useHistoricalIntrinsic ? "Intrinsic (point-in-time model)" : "Intrinsic value / share",
                data: intrinsicSeries,
                yAxis: 0,
                color: BRAND.success,
                lineWidth: 1.6,
                dashStyle: useHistoricalIntrinsic ? "Solid" : "ShortDot",
                tooltip: { valueDecimals: 2, valuePrefix: "$" },
                dataGrouping: { enabled: false },
                step: useHistoricalIntrinsic ? "left" : false,
              },
            ]
          : []),
        ...(hasIntrinsic && deviationSeries.length
          ? [
              {
                type: "line",
                name: "Deviation",
                data: deviationSeries,
                yAxis: 1,
                color: "#f472b6",
                lineWidth: 1.2,
                dataGrouping: { enabled: false },
                step: useHistoricalIntrinsic ? "left" : false,
                tooltip: {
                  valueDecimals: 2,
                  valueSuffix: "%",
                  headerFormat: '<span style="font-size: 11px">{point.key}</span><br/>',
                },
              },
            ]
          : []),
      ],
      credits: { enabled: false },
      time: { useUTC: true },
    };
  }, [priceSeries, intrinsicSeries, deviationSeries, computedIv, isMobile, useHistoricalIntrinsic, logScale]);

  const onSearchSelect = (item) => {
    if (item?.symbol) setTicker(item.symbol);
  };

  const lastDeviation = useMemo(() => {
    for (let i = deviationSeries.length - 1; i >= 0; i--) {
      const v = deviationSeries[i][1];
      if (v != null && Number.isFinite(v)) return v;
    }
    return null;
  }, [deviationSeries]);

  const hasIntrinsicForBacktest = useMemo(
    () => intrinsicSeries.some((p) => p[1] != null && Number.isFinite(p[1]) && p[1] > 0),
    [intrinsicSeries]
  );

  const deviationBacktest = useMemo(() => {
    if (!pxSec.length || !hasIntrinsicForBacktest) return null;
    const init = Number(backtestInitial);
    const dca = Number(backtestDcaDaily);
    return runDeviationDcaBacktest(pxSec, intrinsicSeries, {
      initialCapital: Number.isFinite(init) && init > 0 ? init : 10000,
      dcaDailyUsd: Number.isFinite(dca) && dca >= 0 ? dca : 100,
    });
  }, [pxSec, intrinsicSeries, hasIntrinsicForBacktest, backtestInitial, backtestDcaDaily]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: isMobile ? 22 : 26, fontWeight: 700, color: BRAND.text }}>
        Intrinsic value lab
      </h1>
      <p style={{ margin: "0 0 20px", color: BRAND.textSecondary, fontSize: 14, lineHeight: 1.55 }}>
        Pick a ticker, choose a valuation template, enter your assumptions, then plot your implied value per share on the same price scale. The lower pane shows{" "}
        <strong style={{ color: BRAND.text }}>(price − intrinsic) / intrinsic</strong> as a percentage. This is educational—not investment advice.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 360px) 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        <div
          style={{
            background: BRAND.panel,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 16,
            padding: 16,
          }}
        >
          <label style={{ display: "block", marginBottom: 8, fontSize: 13, color: BRAND.muted }}>Ticker</label>
          <SearchBar key={ticker} onSelect={onSearchSelect} initialValue={ticker} placeholder="Search S&P 500…" />

          <label style={{ display: "block", marginTop: 16, marginBottom: 8, fontSize: 13, color: BRAND.muted }}>Model</label>
          <select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${BRAND.border}`,
              background: BRAND.bg,
              color: BRAND.text,
              fontSize: 14,
            }}
          >
            {INTRINSIC_MODEL_ORDER.map((id) => (
              <option key={id} value={id}>
                {INTRINSIC_MODELS[id].label}
              </option>
            ))}
          </select>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BRAND.border}` }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600, color: BRAND.text }}>About this model</h2>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: BRAND.textSecondary, lineHeight: 1.5 }}>{meta.description}</p>
            <p style={{ margin: "0 0 6px", fontSize: 12, color: BRAND.muted, fontWeight: 600 }}>Formula</p>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: BRAND.textSecondary, lineHeight: 1.45, fontFamily: "ui-monospace, monospace" }}>{meta.formula}</p>
            {meta.abbreviations?.length > 0 && (
              <>
                <p style={{ margin: "0 0 6px", fontSize: 12, color: BRAND.muted, fontWeight: 600 }}>Abbreviations</p>
                <ul style={{ margin: "0 0 12px", paddingLeft: 18, color: BRAND.textSecondary, fontSize: 12, lineHeight: 1.45 }}>
                  {meta.abbreviations.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </>
            )}
            <p style={{ margin: "0 0 6px", fontSize: 12, color: BRAND.muted, fontWeight: 600 }}>Key assumptions</p>
            <ul style={{ margin: 0, paddingLeft: 18, color: BRAND.textSecondary, fontSize: 12, lineHeight: 1.45 }}>
              {meta.assumptions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div
            style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 16,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: BRAND.text }}>Inputs</h2>
            {fundLoading && <p style={{ margin: "0 0 10px", fontSize: 13, color: BRAND.muted }}>Loading fundamentals…</p>}
            {fundError && <p style={{ margin: "0 0 10px", fontSize: 13, color: BRAND.danger }}>{fundError}</p>}
            {!fundLoading && labData?.snapshots?.length > 0 && (
              <p style={{ margin: "0 0 10px", fontSize: 13, color: BRAND.textSecondary, lineHeight: 1.45 }}>
                Filled from <strong style={{ color: BRAND.text }}>reported statements</strong> (TTM where noted) and{" "}
                <strong style={{ color: BRAND.text }}>suggested</strong> discount/growth rates. FCFE uses the same free-cash-flow base as FCFF (reported FCF); adjusted NAV uses GAAP book value per share as a starting point—not fair-value adjusted.
                {labData.beta != null && Number.isFinite(labData.beta) && (
                  <span> Beta (from ratios): {labData.beta.toFixed(2)}.</span>
                )}
              </p>
            )}
            {!fundLoading && labData && (!labData.snapshots || labData.snapshots.length === 0) && (
              <p style={{ margin: "0 0 10px", fontSize: 13, color: BRAND.warning }}>
                Not enough quarterly history to auto-fill; use defaults or enter values manually.
              </p>
            )}
            {labData?.suggestionNotes?.length > 0 && (
              <details style={{ margin: "0 0 12px", fontSize: 12, color: BRAND.textSecondary }}>
                <summary style={{ cursor: "pointer", color: BRAND.accent }}>How suggestions are computed</summary>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.45 }}>
                  {labData.suggestionNotes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </details>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 12,
                cursor: labData?.snapshots?.length ? "pointer" : "not-allowed",
                opacity: labData?.snapshots?.length ? 1 : 0.55,
              }}
            >
              <input
                type="checkbox"
                checked={useHistoricalIntrinsic && !!labData?.snapshots?.length}
                disabled={!labData?.snapshots?.length}
                onChange={(e) => setUseHistoricalIntrinsic(e.target.checked)}
              />
              <span style={{ fontSize: 13, color: BRAND.textSecondary, lineHeight: 1.45 }}>
                Plot intrinsic through time using <strong style={{ color: BRAND.text }}>each quarter’s reported inputs</strong> (steps when new filings arrive). Turn off to use the numbers below with &quot;Update chart&quot; (flat or CAGR path).
              </span>
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <button
                type="button"
                onClick={handleFillFromData}
                disabled={!labData?.filledByModel?.[modelId]}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${BRAND.border}`,
                  background: BRAND.bg,
                  color: BRAND.text,
                  fontSize: 13,
                  cursor: labData?.filledByModel?.[modelId] ? "pointer" : "not-allowed",
                }}
              >
                Reset inputs from data
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              {meta.fields.map((f) => (
                <label key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 12, color: BRAND.muted }}>{f.label}</span>
                  <input
                    type="number"
                    step={f.step ?? "any"}
                    value={fieldValues[f.key] ?? ""}
                    onChange={(e) =>
                      setFieldValues((prev) => ({
                        ...prev,
                        [f.key]: e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: `1px solid ${BRAND.border}`,
                      background: BRAND.bg,
                      color: BRAND.text,
                      fontSize: 14,
                    }}
                  />
                </label>
              ))}
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 14, opacity: useHistoricalIntrinsic ? 0.5 : 1 }}>
              <span style={{ fontSize: 12, color: BRAND.muted }}>
                Intrinsic path CAGR (% per year, manual mode only). 0 = flat line at the value from &quot;Update chart&quot;; non-zero tilts over the price window.
              </span>
              <input
                type="number"
                step={0.1}
                value={intrinsicCagrPct}
                disabled={useHistoricalIntrinsic}
                onChange={(e) => setIntrinsicCagrPct(e.target.value === "" ? 0 : Number(e.target.value))}
                style={{
                  maxWidth: 200,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: `1px solid ${BRAND.border}`,
                  background: BRAND.bg,
                  color: BRAND.text,
                  fontSize: 14,
                }}
              />
            </label>

            {computeError && (
              <p style={{ margin: "12px 0 0", color: BRAND.danger, fontSize: 13 }}>{computeError}</p>
            )}

            <button
              type="button"
              onClick={handleApply}
              style={{
                marginTop: 16,
                padding: "10px 20px",
                borderRadius: 10,
                border: "none",
                background: BRAND.accent,
                color: BRAND.text,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Update chart
            </button>

            {displayIv != null && Number.isFinite(displayIv) && (
              <p style={{ margin: "14px 0 0", fontSize: 14, color: BRAND.textSecondary }}>
                {useHistoricalIntrinsic && intrinsicSeries.some((p) => p[1] != null)
                  ? "Latest implied value (end of series, from reported inputs): "
                  : "Implied value (manual, start of chart before CAGR): "}
                <strong style={{ color: BRAND.success }}>${displayIv.toFixed(2)}</strong> per share
              </p>
            )}
          </div>

          <div
            style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 16,
              padding: 12,
              minHeight: 200,
            }}
          >
            {priceLoading && <p style={{ color: BRAND.muted, margin: 8 }}>Loading price…</p>}
            {priceError && <p style={{ color: BRAND.danger, margin: 8 }}>{priceError}</p>}
            {!priceLoading && !priceError && !priceSeries.length && (
              <p style={{ color: BRAND.muted, margin: 8 }}>No price data for this ticker.</p>
            )}
            {priceSeries.length > 0 && (
              <>
                <div
                  style={{
                    margin: "0 0 10px 8px",
                    paddingLeft: "2ch",
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 16,
                    color: BRAND.text,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  {companyLogoUrl ? (
                    <img
                      src={companyLogoUrl}
                      alt=""
                      style={companyLogoTitleStyle(isMobile ? COMPANY_LOGO_TITLE_SIZE_MOBILE : COMPANY_LOGO_TITLE_SIZE)}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : null}
                  <span>
                    {ticker.trim().toUpperCase()} — Price vs{" "}
                    {INTRINSIC_MODELS[modelId]?.label || "Intrinsic value"}
                  </span>
                </div>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    margin: "0 0 10px 8px",
                    cursor: "pointer",
                    fontSize: 13,
                    color: BRAND.textSecondary,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={logScale}
                    onChange={(e) => setLogScale(e.target.checked)}
                  />
                  Log scale for price &amp; intrinsic (linear deviation pane below)
                </label>
                <HighchartsReact
                  highcharts={Highcharts}
                  constructorType="stockChart"
                  options={chartOptions}
                  ref={chartRef}
                />
                {displayIv != null && Number.isFinite(lastDeviation) && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: BRAND.bg,
                      border: `1px solid ${BRAND.border}`,
                      fontSize: 13,
                      color: BRAND.textSecondary,
                    }}
                  >
                    <strong style={{ color: BRAND.text }}>Latest deviation: </strong>
                    {lastDeviation > 0 ? (
                      <span>
                        Price is{" "}
                        <span style={{ color: BRAND.danger, fontWeight: 600 }}>+{lastDeviation.toFixed(2)}%</span> above intrinsic (overvalued vs your model).
                      </span>
                    ) : (
                      <span>
                        Price is{" "}
                        <span style={{ color: BRAND.success, fontWeight: 600 }}>{lastDeviation.toFixed(2)}%</span> vs intrinsic (negative = below your implied value).
                      </span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    padding: "14px 14px 16px",
                    borderRadius: 12,
                    background: BRAND.bg,
                    border: `1px solid ${BRAND.border}`,
                  }}
                >
                  <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: BRAND.text }}>
                    Strategy backtest (deviation rule)
                  </h3>
                  <p style={{ margin: "0 0 12px", fontSize: 12, color: BRAND.textSecondary, lineHeight: 1.5 }}>
                    Same daily bars as the chart. While <strong style={{ color: BRAND.text }}>(price − intrinsic) / intrinsic</strong> is{" "}
                    <strong style={{ color: BRAND.success }}>negative</strong> (price below model), invest up to the daily DCA amount from cash. When deviation is{" "}
                    <strong style={{ color: BRAND.text }}>zero or positive</strong>, sell the full position at that close. No trades on days without intrinsic.                     The
                    comparison benchmark uses your <strong style={{ color: BRAND.text }}>average in-position streak length</strong> (trading days with shares at close)
                    and asks: &quot;if I bought at random past closes and held that many sessions, what simple return would I get on average?&quot; The strategy side reports{" "}
                    <strong style={{ color: BRAND.text }}>mean and median simple return per position</strong> (proceeds or mark vs cash DCA’d into that position). Cumulative
                    return on starting cash is shown separately—it is not the same scale as the hop stats. Educational only—not investment advice.
                  </p>
                  {!hasIntrinsicForBacktest && (
                    <p style={{ margin: 0, fontSize: 13, color: BRAND.muted }}>
                      Load a model with a visible intrinsic series (historical or manual &quot;Update chart&quot;) to run the backtest.
                    </p>
                  )}
                  {hasIntrinsicForBacktest && deviationBacktest?.ok && (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                          gap: 10,
                          marginBottom: 12,
                        }}
                      >
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: BRAND.muted }}>Starting cash ($)</span>
                          <input
                            type="number"
                            min={1}
                            step={100}
                            value={backtestInitial}
                            onChange={(e) => setBacktestInitial(e.target.value === "" ? 0 : Number(e.target.value))}
                            style={{
                              padding: "8px 10px",
                              borderRadius: 8,
                              border: `1px solid ${BRAND.border}`,
                              background: BRAND.panel,
                              color: BRAND.text,
                              fontSize: 14,
                            }}
                          />
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, color: BRAND.muted }}>Daily DCA ($) while undervalued</span>
                          <input
                            type="number"
                            min={0}
                            step={10}
                            value={backtestDcaDaily}
                            onChange={(e) => setBacktestDcaDaily(e.target.value === "" ? 0 : Number(e.target.value))}
                            style={{
                              padding: "8px 10px",
                              borderRadius: 8,
                              border: `1px solid ${BRAND.border}`,
                              background: BRAND.panel,
                              color: BRAND.text,
                              fontSize: 14,
                            }}
                          />
                        </label>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                          gap: 12,
                          fontSize: 13,
                          color: BRAND.textSecondary,
                        }}
                      >
                        <div>
                          <span style={{ color: BRAND.muted, fontSize: 12 }}>Strategy (per position — same simple-return idea as H-hop)</span>
                          <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 4, lineHeight: 1.4, marginBottom: 8 }}>
                            Each round-trip: simple return = (sale proceeds − cash DCA’d in) ÷ cash DCA’d in. Open position at end uses mark-to-market vs cost.{" "}
                            <strong style={{ color: BRAND.text }}>{deviationBacktest.strategyEpisodeCount ?? 0}</strong> episode
                            {(deviationBacktest.strategyEpisodeCount ?? 0) === 1 ? "" : "s"}.
                          </div>
                          {deviationBacktest.strategyEpisodeCount > 0 &&
                          deviationBacktest.strategyMeanEpisodeReturnPct != null ? (
                            <div style={{ fontSize: 13, color: BRAND.textSecondary }}>
                              Mean:{" "}
                              <span
                                style={{
                                  color: deviationBacktest.strategyMeanEpisodeReturnPct >= 0 ? BRAND.success : BRAND.danger,
                                  fontWeight: 600,
                                }}
                              >
                                {deviationBacktest.strategyMeanEpisodeReturnPct >= 0 ? "+" : ""}
                                {deviationBacktest.strategyMeanEpisodeReturnPct.toFixed(2)}%
                              </span>
                              {deviationBacktest.strategyMedianEpisodeReturnPct != null && (
                                <span style={{ marginLeft: 10 }}>
                                  Median:{" "}
                                  <span
                                    style={{
                                      color: deviationBacktest.strategyMedianEpisodeReturnPct >= 0 ? BRAND.success : BRAND.danger,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {deviationBacktest.strategyMedianEpisodeReturnPct >= 0 ? "+" : ""}
                                    {deviationBacktest.strategyMedianEpisodeReturnPct.toFixed(2)}%
                                  </span>
                                </span>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: BRAND.muted }}>No completed or open position with deployed cash—episode returns N/A.</div>
                          )}
                          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${BRAND.border}`, fontSize: 11, color: BRAND.muted }}>
                            <span style={{ display: "block", marginBottom: 4 }}>Whole backtest (starting cash ${deviationBacktest.initial.toLocaleString()}, ${deviationBacktest.dcaDaily.toFixed(0)}/day when undervalued)</span>
                            <span style={{ color: BRAND.textSecondary }}>Ending equity </span>
                            <span style={{ color: BRAND.text, fontWeight: 600 }}>${deviationBacktest.finalEquity.toFixed(2)}</span>
                            <span style={{ color: BRAND.textSecondary }}> · Cumulative return on that stake </span>
                            <span
                              style={{
                                color: deviationBacktest.totalReturnPct >= 0 ? BRAND.success : BRAND.danger,
                                fontWeight: 600,
                              }}
                            >
                              {deviationBacktest.totalReturnPct >= 0 ? "+" : ""}
                              {deviationBacktest.totalReturnPct.toFixed(2)}%
                            </span>
                          </div>
                        </div>
                        <div>
                          <span style={{ color: BRAND.muted, fontSize: 12 }}>H-day hop benchmark (matched holding length)</span>
                          <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 4, lineHeight: 1.4 }}>
                            Average streak with shares (EOD):{" "}
                            <strong style={{ color: BRAND.text }}>
                              {deviationBacktest.avgHoldingDaysTrading != null
                                ? `${deviationBacktest.avgHoldingDaysTrading.toFixed(1)} trading days`
                                : "—"}
                            </strong>
                            {deviationBacktest.holdingStreakCount != null && deviationBacktest.holdingStreakCount > 0 && (
                              <span> ({deviationBacktest.holdingStreakCount} separate position{deviationBacktest.holdingStreakCount === 1 ? "" : "s"})</span>
                            )}
                            . Rounded to <strong style={{ color: BRAND.text }}>H = {deviationBacktest.benchmarkH ?? "—"}</strong> sessions: mean and median simple return
                            across all overlapping buys (close → close <span style={{ whiteSpace: "nowrap" }}>H sessions</span> later), price only.
                          </div>
                          {deviationBacktest.benchmarkHopCount > 0 &&
                          deviationBacktest.benchmarkMeanHopReturnPct != null ? (
                            <>
                              <div style={{ marginTop: 10, fontSize: 13, color: BRAND.textSecondary }}>
                                Mean hop return:{" "}
                                <span
                                  style={{
                                    color: deviationBacktest.benchmarkMeanHopReturnPct >= 0 ? BRAND.success : BRAND.danger,
                                    fontWeight: 600,
                                  }}
                                >
                                  {deviationBacktest.benchmarkMeanHopReturnPct >= 0 ? "+" : ""}
                                  {deviationBacktest.benchmarkMeanHopReturnPct.toFixed(2)}%
                                </span>
                                {deviationBacktest.benchmarkMedianHopReturnPct != null && (
                                  <span style={{ marginLeft: 10 }}>
                                    Median:{" "}
                                    <span
                                      style={{
                                        color: deviationBacktest.benchmarkMedianHopReturnPct >= 0 ? BRAND.success : BRAND.danger,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {deviationBacktest.benchmarkMedianHopReturnPct >= 0 ? "+" : ""}
                                      {deviationBacktest.benchmarkMedianHopReturnPct.toFixed(2)}%
                                    </span>
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 6 }}>
                                ({deviationBacktest.benchmarkHopCount} overlapping windows in this price history)
                              </div>
                            </>
                          ) : (
                            <div style={{ marginTop: 8, fontSize: 12, color: BRAND.muted }}>
                              {deviationBacktest.holdingStreakCount === 0
                                ? "No completed in-position streaks (never held shares)—benchmark N/A."
                                : "Not enough price history for an H-session window—benchmark N/A."}
                            </div>
                          )}
                        </div>
                      </div>
                      <p style={{ margin: "12px 0 0", fontSize: 12, color: BRAND.muted }}>
                        Full exits (deviation ≥ 0): <strong style={{ color: BRAND.text }}>{deviationBacktest.sellCount}</strong>
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
